import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  MapPin, 
  Ruler, 
  Tag, 
  ArrowRight,
  ShieldCheck,
  Calendar,
  Sparkles,
  Layers,
  Quote,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { Button } from './Button';
import { PROJECT_META } from '../data/projects';
import { useTheme } from '../context/ThemeContext';
import { SEO } from './SEO';

const PAGE_SIZE = 8;

interface LazyGalleryImageProps {
  src: string;
  alt: string;
  onClick: () => void;
  index: number;
  total: number;
  tag: string;
  isDark: boolean;
}

/** Lazy loaded image tile with editorial reveal transitions */
function LazyGalleryImage({
  src,
  alt,
  onClick,
  index,
  total,
  tag,
  isDark
}: LazyGalleryImageProps) {
  const [loaded, setLoaded] = useState(false);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 15 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      className={`group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer border ${
        isDark ? 'border-white/[0.04] bg-[#0E1626]' : 'border-slate-200/50 bg-slate-100'
      }`}
      onClick={onClick}
    >
      {/* Skeleton shimmer before load */}
      {!loaded && (
        <div className="absolute inset-0 skeleton-shimmer rounded-2xl" />
      )}

      {/* Main Image Asset */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-105 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Cinematic Glassmorphic Gradient Overlay on Hover */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex flex-col justify-end p-5 select-none" />

      {/* Visual Text Details (slide-up reveal on hover) */}
      <div className="absolute inset-x-5 bottom-5 z-10 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-350 ease-out flex flex-col select-none">
        <span className="text-[10px] uppercase font-poppins tracking-widest font-extrabold text-accent mb-1">
          {tag}
        </span>
        <div className="flex justify-between items-center text-white">
          <span className="font-poppins text-xs font-semibold">View Detail</span>
          <span className="text-[10px] font-bold text-white/50 tracking-wider">
            {index + 1} / {total}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export function CaseStudyPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [activeTag, setActiveTag] = useState<string>('All');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const project = slug ? PROJECT_META[slug] : null;

  // Scroll to top instantly on project navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);



  if (!project) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#0B0F1A]' : 'bg-slate-50'}`}>
        <h1 className={`text-3xl font-poppins font-black mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Case Study Not Found</h1>
        <p className={`mb-8 font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>The project specification does not exist in our systems.</p>
        <Button variant="primary" onClick={() => navigate('/')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Return to Homepage
        </Button>
      </div>
    );
  }

  // Derive unique tags inside gallery
  const uniqueTags = useMemo(() => {
    const tags = [...new Set(project.gallery.map((g) => g.tag))];
    return ['All', ...tags];
  }, [project]);

  // Filter gallery items
  const filteredGallery = useMemo(() =>
    activeTag === 'All'
      ? project.gallery
      : project.gallery.filter((g) => g.tag === activeTag),
    [activeTag, project.gallery]
  );

  const handleTagChange = useCallback((tag: string) => {
    setActiveTag(tag);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const visibleGallery = filteredGallery.slice(0, visibleCount);
  const hasMore = visibleCount < filteredGallery.length;

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  
  const nextImage = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % filteredGallery.length);
    }
  };

  const prevImage = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex - 1 + filteredGallery.length) % filteredGallery.length);
    }
  };

  // Lightbox key binds
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, filteredGallery]);

  const seoTitle = project ? `${project.title} — Premium Case Study | Aura Spaces` : 'Aura Spaces | Premium Case Study';
  const seoDescription = project 
    ? `${project.description.slice(0, 150)}... Explore this luxury ${project.category} finish in ${project.location} by Aura Spaces.` 
    : 'Premium Case Study from Aura Spaces.';
  const seoKeywords = project 
    ? `${project.title}, ${project.category}, ${project.location}, interior design case study, Aura Spaces` 
    : 'interior design case study, Aura Spaces';
  const canonicalUrl = project ? `https://www.auraspaces.net/case-study/${slug}` : undefined;

  return (
    <div className={`min-h-screen transition-colors duration-500 relative overflow-hidden ${
      isDark ? 'bg-[#0B0F1A] text-gray-200' : 'bg-slate-50 text-slate-800'
    }`}>
      <SEO 
        title={seoTitle} 
        description={seoDescription} 
        keywords={seoKeywords} 
        canonicalUrl={canonicalUrl} 
      />
      
      {/* Decorative Blurs */}
      <div className="absolute top-1/3 -left-48 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-48 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Immersive Editorial Hero Block */}
      <div className={`relative min-h-[65vh] sm:min-h-[500px] flex items-center mt-[72px] transition-colors duration-500 overflow-hidden ${
        isDark ? 'bg-[#0F172A]' : 'bg-slate-100'
      }`}>
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(201,162,107,0.3),rgba(255,255,255,0))]" />
        
        {/* Large ambient blur background of the image for visual depth */}
        <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-0">
          <img 
            src={project.hero} 
            alt="Blur Backdrop" 
            className="w-full h-full object-cover blur-[100px] opacity-15 dark:opacity-20 scale-110"
          />
        </div>

        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 md:py-20 relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            
            {/* Left Column: Premium Text block */}
            <div className="lg:col-span-5 flex flex-col justify-center space-y-6">
              
              {/* Back Button inside text column */}
              <div>
                <button
                  onClick={() => { 
                    navigate('/'); 
                    setTimeout(() => {
                      document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
                    }, 150);
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-xs uppercase font-poppins tracking-wider font-extrabold shadow-sm transition-all cursor-pointer ${
                    isDark 
                      ? 'border-white/10 bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white' 
                      : 'border-slate-200 bg-white text-slate-650 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <ArrowLeft className="w-4 h-4 text-accent" /> Back to Portfolio
                </button>
              </div>

              <motion.div
                initial={{ opacity: 0, x: -35 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                className="space-y-4"
              >
                {/* Breadcrumbs / Tag */}
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase font-poppins tracking-widest font-extrabold px-3 py-1 bg-accent/15 border border-accent/25 rounded-md text-accent`}>
                    {project.category}
                  </span>
                  <span className={`text-[10px] uppercase font-poppins tracking-widest font-extrabold text-gray-400 dark:text-gray-500`}>
                    • Case Study
                  </span>
                </div>

                {/* Main Heading with dynamic styling */}
                <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-poppins font-black leading-none tracking-tight ${
                  isDark ? 'text-white' : 'text-slate-950'
                }`}>
                  {project.title}
                </h1>

                {/* Subtitle location */}
                <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wide">
                  <MapPin className="w-4 h-4 text-accent shrink-0" />
                  <span className={isDark ? 'text-gray-400' : 'text-slate-500'}>
                    {project.location}
                  </span>
                </div>

                <p className={`text-sm leading-relaxed max-w-md pt-2 ${
                  isDark ? 'text-gray-450' : 'text-slate-600'
                }`}>
                  Explore how we transformed this layout from conceptual designs into a stunning finished space using premium modular cabinets and custom furniture.
                </p>
              </motion.div>

            </div>

            {/* Right Column: Premium framed image showcase with micro-shadows */}
            <div className="lg:col-span-7 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
              >
                {/* Visual backdrop shadow decoration */}
                <div className="absolute -inset-1.5 bg-gradient-to-r from-accent/20 to-[#E0B880]/10 rounded-[32px] blur-xl opacity-70 pointer-events-none" />

                {/* Image frame */}
                <div className={`relative rounded-[28px] overflow-hidden aspect-[16/10] sm:aspect-[16/9] border shadow-2xl group ${
                  isDark ? 'border-white/[0.08]' : 'border-slate-200/80'
                }`}>
                  
                  <img
                    src={project.hero}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.03]"
                  />

                  {/* Soft bottom dark gradient for visual aesthetics */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

                  {/* Micro stat overlays inside the frame */}
                  <div className="absolute bottom-5 left-5 right-5 flex justify-between items-center text-white pointer-events-none select-none z-10">
                    <span className="text-[10px] uppercase font-poppins tracking-widest font-extrabold text-white/80">
                      Completed Spec: {project.area}
                    </span>
                    <span className="text-[10px] uppercase font-poppins tracking-widest font-extrabold text-accent flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> Handover Ready
                    </span>
                  </div>

                </div>
              </motion.div>
            </div>

          </div>
        </div>

      </div>

      {/* Asymmetrical Split Case Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 relative z-10">
        
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start mb-24">
          
          {/* Left sticky column: Project DNA Summary Card */}
          <div className="lg:col-span-4 lg:sticky lg:top-28 z-10">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className={`rounded-3xl p-6 sm:p-8 border relative overflow-hidden ${
                isDark 
                  ? 'bg-[#111827]/85 border-white/[0.06] shadow-2xl' 
                  : 'bg-white border-slate-200/50 shadow-xl shadow-slate-100/40'
              }`}
            >
              <div className="absolute -top-12 -left-12 w-28 h-28 bg-accent/5 rounded-full blur-2xl" />
              
              <h3 className={`text-xs uppercase font-poppins tracking-widest font-extrabold mb-6 pb-3 border-b ${
                isDark ? 'text-gray-400 border-white/5' : 'text-slate-500 border-slate-100'
              }`}>
                PROJECT DATA OVERVIEW
              </h3>

              <div className="space-y-6">
                {[
                  { label: "Design Theme", value: project.category === "Home Interiors" ? "Contemporary Minimalist" : "Bespoke Corporate Office", icon: Layers },
                  { label: "Total Carpet Area", value: project.area, icon: Ruler },
                  { label: "Project Location", value: project.location, icon: MapPin },
                  { label: "Delivery Duration", value: project.category === "Home Interiors" ? "45 Business Days" : "60 Business Days", icon: Clock }
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center shrink-0">
                      <Icon className="w-4.5 h-4.5 text-accent" />
                    </div>
                    <div>
                      <span className={`text-[10px] uppercase font-poppins font-extrabold tracking-wider block leading-tight ${
                        isDark ? 'text-gray-500' : 'text-slate-400'
                      }`}>
                        {label}
                      </span>
                      <strong className={`font-poppins text-[15px] font-black ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}>
                        {value}
                      </strong>
                    </div>
                  </div>
                ))}
              </div>

              <div className={`mt-8 pt-6 border-t flex items-center gap-2.5 text-xs font-semibold ${
                isDark ? 'text-gray-400 border-white/5' : 'text-slate-500 border-slate-100'
              }`}>
                <ShieldCheck className="w-5 h-5 text-accent shrink-0" />
                <span>Snag-Free Delivery Guaranteed</span>
              </div>
            </motion.div>
          </div>

          {/* Right column: Narrative and Specifications */}
          <div className="lg:col-span-8 space-y-12">
            
            {/* Story block */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="space-y-6"
            >
              <h2 className={`text-2xl sm:text-3xl font-poppins font-black leading-tight tracking-tight ${
                isDark ? 'text-white' : 'text-slate-950'
              }`}>
                The Design Narrative
              </h2>
              
              <p className={`text-base sm:text-lg leading-relaxed font-medium ${
                isDark ? 'text-gray-450' : 'text-slate-600'
              }`}>
                {project.description}
              </p>

              {/* Designer custom quote */}
              <div className={`rounded-3xl p-6 sm:p-8 border relative overflow-hidden my-8 ${
                isDark 
                  ? 'bg-white/[0.02] border-white/[0.04]' 
                  : 'bg-slate-100 border-slate-200/50'
              }`}>
                <Quote className="absolute right-6 top-6 w-24 h-24 text-accent/5 pointer-events-none" />
                <p className={`text-sm sm:text-base italic leading-relaxed mb-4 relative z-10 font-medium ${
                  isDark ? 'text-gray-300' : 'text-slate-800'
                }`}>
                  "Our conceptual layouts for this layout prioritised natural light infiltration and fluid structural lines. We engineered custom sliding panel closets and false ceiling profiles to make the space feel unified, expansive, and high-impact."
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-accent" />
                  <span className={`text-xs uppercase font-poppins tracking-wider font-extrabold ${
                    isDark ? 'text-gray-450' : 'text-slate-500'
                  }`}>
                    Chief Design Architect, Aura Spaces
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Spec grid */}
            {project.specs && project.specs.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6 pt-6"
              >
                <h3 className={`text-2xl font-poppins font-black leading-tight tracking-tight ${
                  isDark ? 'text-white' : 'text-slate-950'
                }`}>
                  Bespoke Work Specifications
                </h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {project.specs.map((spec, i) => (
                    <div 
                      key={i} 
                      className={`rounded-2xl p-5 border flex gap-3.5 leading-snug font-semibold ${
                        isDark 
                          ? 'bg-[#111827]/40 border-white/[0.04]' 
                          : 'bg-white border-slate-200/40 shadow-xs text-slate-700'
                      }`}
                    >
                      <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{spec}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

          </div>

        </div>

        {/* Editorial Photo Gallery */}
        <div className="mb-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 sm:mb-12">
            <div>
              <h2 className={`text-2xl sm:text-3xl font-poppins font-black tracking-tight ${
                isDark ? 'text-white' : 'text-slate-950'
              }`}>
                Bespoke Photo Gallery
              </h2>
              <p className={`text-sm font-medium mt-1 ${isDark ? 'text-gray-500' : 'text-slate-450'}`}>
                Discover {project.gallery.length} customized detail captures from this build.
              </p>
            </div>

            {/* Tag Filter Pills */}
            {uniqueTags.length > 2 && (
              <div className="flex flex-wrap gap-2">
                {uniqueTags.map((tag) => {
                  const isActive = activeTag === tag;
                  return (
                    <button
                      key={tag}
                      onClick={() => handleTagChange(tag)}
                      className={`px-4.5 py-2.5 rounded-full text-xs font-poppins tracking-wider font-extrabold uppercase border transition-all duration-300 transform hover:scale-[1.02] cursor-pointer ${
                        isActive
                          ? 'bg-accent text-white border-accent shadow-md shadow-accent/20'
                          : isDark
                            ? 'bg-[#0E1626] border-white/5 text-gray-400 hover:text-white hover:border-white/10'
                            : 'bg-white border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400 shadow-xs'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Mosaic Grid */}
          <motion.div 
            layout 
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5"
          >
            <AnimatePresence>
              {visibleGallery.map((item, index) => (
                <LazyGalleryImage
                  key={item.src}
                  src={item.src}
                  alt={`${project.title} – ${item.tag}`}
                  tag={item.tag}
                  index={index}
                  total={filteredGallery.length}
                  isDark={isDark}
                  onClick={() => openLightbox(index)}
                />
              ))}
            </AnimatePresence>
          </motion.div>

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center mt-12">
              <button
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl bg-transparent hover:bg-accent text-accent hover:text-white border-2 border-accent text-xs uppercase font-poppins tracking-wider font-extrabold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
              >
                Load More Captures
                <ChevronRight className="w-4.5 h-4.5" />
              </button>
            </div>
          )}
        </div>

        {/* Radial backing visual CTA card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.6 }} 
          className="bg-primary rounded-3xl p-8 sm:p-16 text-center relative overflow-hidden shadow-2xl border border-white/[0.04]"
        >
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-accent/10 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/2 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2 pointer-events-none" />
          
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-poppins font-black text-white leading-tight mb-4">
              Love What You See?
            </h2>
            <p className="text-gray-350 text-base sm:text-lg mb-8 leading-relaxed font-medium">
              We design and execute custom furniture, modular kitchen systems, and turnkey transformations. Get a comprehensive quote customized for your space.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <Button 
                variant="accent" 
                size="md" 
                onClick={() => { 
                  navigate('/'); 
                  setTimeout(() => {
                    document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' });
                  }, 200); 
                }}
                className="w-full sm:w-auto flex items-center justify-center font-extrabold tracking-wider px-6 py-3 rounded-xl"
              >
                Book Design Consultation 
                <ArrowRight className="ml-2 w-4 h-4 shrink-0 text-white" />
              </Button>
              <Button 
                variant="glass" 
                size="md" 
                onClick={() => { 
                  navigate('/'); 
                  setTimeout(() => {
                    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
                  }, 200); 
                }}
                className="w-full sm:w-auto border-white/20 hover:bg-white/15 text-white flex items-center justify-center font-extrabold tracking-wider px-6 py-3 rounded-xl"
              >
                Explore Other Layouts
              </Button>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Fullscreen Interactive Lightbox Modal Portal */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-2000 bg-black/95 backdrop-blur-md flex items-center justify-center select-none" 
            onClick={closeLightbox}
          >
            {/* Lightbox top title bar */}
            <div className="absolute top-0 left-0 right-0 p-5 flex items-center justify-between z-10 bg-gradient-to-b from-black/80 to-transparent">
              <div className="text-left">
                <h4 className="text-white font-poppins font-black text-base tracking-tight leading-none">
                  {project.title}
                </h4>
                <p className="text-accent text-xs font-poppins font-bold uppercase tracking-wider mt-1.5">
                  Category: {project.category} • Feature: {filteredGallery[lightboxIndex].tag}
                </p>
              </div>
              
              {/* Close Button */}
              <button 
                onClick={closeLightbox} 
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center transition-colors text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Left Prev Arrow Trigger */}
            <button 
              onClick={(e) => { e.stopPropagation(); prevImage(); }} 
              className="absolute left-4 sm:left-8 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center transition-colors text-white z-10 cursor-pointer"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Center Visual Slide frame */}
            <div className="max-w-[85vw] max-h-[75vh] flex items-center justify-center p-2 relative" onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="wait">
                <motion.img 
                  key={lightboxIndex} 
                  src={filteredGallery[lightboxIndex].src} 
                  alt={`${project.title} – Zoomed Capture`}
                  initial={{ opacity: 0, scale: 0.96 }} 
                  animate={{ opacity: 1, scale: 1 }} 
                  exit={{ opacity: 0, scale: 0.96 }} 
                  transition={{ duration: 0.35 }} 
                  className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl border border-white/5" 
                />
              </AnimatePresence>
            </div>

            {/* Right Next Arrow Trigger */}
            <button 
              onClick={(e) => { e.stopPropagation(); nextImage(); }} 
              className="absolute right-4 sm:right-8 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center transition-colors text-white z-10 cursor-pointer"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Float progress indicators */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md border border-white/15 px-4.5 py-2 rounded-full text-[10px] font-poppins font-extrabold tracking-widest text-white shadow-md uppercase">
              {lightboxIndex + 1} OF {filteredGallery.length} SPECIFICATIONS
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
